import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas"
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  ComposedChart,
  Cell,
  Bar,
  BarChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  LabelList,
  Legend,
  ReferenceLine,
  ReferenceDot,
} from "recharts";

import Scorecard from "./Scorecard.js";
import { fetchGraphData, fetchStockData, fetchDividendData, fetchQuarterlyData } from "../../helpers/graph_middleware.js";
import { getVal, formatDate, isNegative, setBool, getTableData, formatCurrency } from "../../helpers/process_morningstar.js";
import { Box, Typography, Grid, Paper, Button } from "@mui/material";
import ReturnsTable from "./ReturnsTable.js";

async function captureTableAsPNG(tableRef) {
  if (!tableRef.current) return null;

  const canvas = await html2canvas(tableRef.current, {
    scale: 3,              // sharpness
    backgroundColor: "#fff",
    useCORS: true,
  });

  return canvas.toDataURL("image/png");
}

async function combineImagesVertically(imageUrls) {
  const images = await Promise.all(
    imageUrls.map(src => {
      const img = new Image();
      img.src = src;
      return new Promise(res => img.onload = () => res(img));
    })
  );

  const width = Math.max(...images.map(i => i.width));
  const height = images.reduce((sum, i) => sum + i.height, 0);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = 750;

  const ctx = canvas.getContext("2d");
  let y = 0;

  for (const img of images) {
    ctx.drawImage(img, 0, y);
    y += img.height;
  }

  return canvas.toDataURL("image/png");
}

async function svgToPng(svgDataUrl, width = 600, height = 400, scale = 3) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");

      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      
      const png = canvas.toDataURL("image/png");
      resolve(png);
    };
    img.src = svgDataUrl;
  });
}

async function captureRechartsAsPNGs(chartRefs) {
  const serializer = new XMLSerializer();
  const images = [];

  for (const ref of chartRefs) {
    if (!ref.current) continue;

    // Clone the chart container
    const clone = ref.current.cloneNode(true);
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    clone.style.width = `570px`;
    clone.style.height = `250px`;
    document.body.appendChild(clone);

    // Wait for Recharts to render the clone
    await new Promise((r) => setTimeout(r, 500));

    // Grab all SVGs inside the clone
    const svgs = Array.from(clone.querySelectorAll("svg")).filter(svg => {
      const bbox = svg.getBBox();
      return bbox.width > 100 && bbox.height > 100; // ignore tiny legend icons
    });

    if (!svgs.length) {
      // console.warn("⚠️ No SVGs found in chart clone");
      document.body.removeChild(clone);
      continue;
    }

    const isStackedChart = svgs.length > 1;


    // Combine multiple SVGs vertically
    const combinedCanvas = document.createElement("canvas");
    const drawHeight = isStackedChart ? 250 * 2 : 250 * 3;
    const totalHeight = drawHeight * svgs.length;
    combinedCanvas.width = 570 * 3;   // scale for sharpness
    combinedCanvas.height = totalHeight;
    const ctx = combinedCanvas.getContext("2d");

    let offsetY = 0;
    for (const svg of svgs) {
      const svgString = serializer.serializeToString(svg);
      const base64SVG =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(svgString)));

      const pngData = await svgToPng(base64SVG, 570, 250, 3);

      // Draw PNG onto combined canvas
      const img = new Image();
      img.src = pngData;
      await new Promise((resolve) => (img.onload = resolve));


        ctx.drawImage(
          img,
          0,
          isStackedChart ? offsetY * 2 : offsetY,
          570 * 3,
          drawHeight
        );
        offsetY += 250;
    }

    // Convert combined canvas to final PNG
    const finalPng = combinedCanvas.toDataURL("image/png");
    images.push(finalPng);

    // Remove the offscreen clone
    document.body.removeChild(clone);
  }

  return images;
}

const buttonValues = [
  "5D",  
  "1M", 
  "6M", 
  "YTD",
  "1Y",
  "2Y",
  "5Y",
  "ALL",
];

const financialIndustries = [
  "10320010",
  "10320020",
  "10320030",
  "10340010",
  "10340020",
  "10340030",
  "10340040",
  "10340060",
  "10350010",
  "10350020",
  "10360010",
  "10420010",
  "10420020",
  "10420030",
  "10420040",
  "10420050",
  "10420060",
  "10420070",
  "10420080",
  "10420090"
]

function Graphs({ onImagesReady, descriptionData }) {
    const [graphData, setGraphData] = useState([]);
    const [quarterlyData, setQuarterlyData] = useState([]);
    const [stockData, setStockData] = useState([]);
    const [dividendData, setDividendData] = useState([]);
    const [dividendPrice, setDividendPrice] = useState([]);
    const [basePrice, setBasePrice] = useState([]);
    const [twoYearPrice, setTwoYearPrice] = useState([]);
    const [images, setImages] = useState([]);
    const [scorecard, setScorecard] = useState([]);
    const [capRD, setCapRD] = useState(getVal("capRD"));
    const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));
    const [selectedTax, setSelectedTax] = useState(getVal("selectedTax"));
    const [intangibles, setIntangibles] = useState(getVal("intangibles"));
    const [dateRange, setDateRange] = useState(localStorage.getItem('dateRange'))
    const [industryNumber, setIndustryNumber] = useState()
    let defaultDividendData = [useRef([])];

    const tableRef = useRef();

    const chartRefs = [
      useRef(null),
      useRef(null), 
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null)
    ];

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "intangibles") setIntangibles(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIntangibles(getVal("intangibles"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  const chartPaperStyle = {       
    height: 250,
    px: 1,
  };

  // Fetch data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      const assetClass = await getTableData("Asset Class")
      setIndustryNumber(assetClass[0]['MorningStarIndustryCode'])

      const financialSummaryData = await fetchGraphData(capRD, includeOtherIncome, selectedTax);
      let minLength = 20
      
      const dateCutoff = new Date(financialSummaryData[0].date);

      const stockdividendData = await fetchStockData();
      setDividendPrice(stockdividendData[0].slice(-69));

      const dateRange = stockdividendData[1].filter(item => {
        return new Date(item.date) > dateCutoff;
      });

      let lastYear = financialSummaryData.length > 0
        ? new Date(financialSummaryData[0].date).getFullYear()
        : new Date().getFullYear()
    
      
      while (financialSummaryData.length < minLength) {
        lastYear -= 1
        financialSummaryData.unshift({
            date: `${lastYear}-12-31`, // use the next year
            lastValue: false,
            cfirr: 0,
            cfirrIncl: 0,
            growth: 0,
            growthIncl: 0,
            growthTrend: 0,
            growthTrendIncl: 0,
            totalRevenue: 0,
            grossMargin: 0,
            cashFlow: 0,
            cashFlowShare: 0,
            marketCap: 0,
            debtAndEquivalents: 0,
            debtOverMarketCap: 0,
            grossMarginPercent: 0,
            cashFlowPercent: 0,
            ebitda: 0,
            evOverEbitda: 0,
            evOverCashFlow: 0,
          });
      }     
        
      minLength *= 12
      let firstDate = new Date(dateRange[0].date)

      while (dateRange.length < minLength) {
        firstDate.setMonth(firstDate.getMonth() - 1)

        dateRange.unshift({
          date: firstDate.toISOString().split('T')[0],
          stockPrice: 0
        })
      }

      setGraphData(financialSummaryData);
      setStockData(dateRange);
      setTwoYearPrice(stockdividendData[2]);
      setBasePrice(stockdividendData[0]);
      
      defaultDividendData.current = stockdividendData[0];

      const getdividendData = await fetchDividendData(capRD, includeOtherIncome, selectedTax);
      setDividendData(getdividendData);

      const quarterlyGraphData = await fetchQuarterlyData(capRD, includeOtherIncome, selectedTax)
      const minQuarterLength = 60
      let firstQuarterDate = new Date(quarterlyGraphData[0].date)
      
      while (quarterlyGraphData.length < minQuarterLength) {

        firstQuarterDate.setMonth(firstQuarterDate.getMonth() - 3)

        quarterlyGraphData.unshift({
          date: firstQuarterDate.toISOString().split('T')[0],
          cfirr: 0,
          cfirrIncl: 0,
          growth: 0,
          growthIncl: 0,
          growthTrend: 0,
          growthTrendIncl: 0
        })

      }

      setQuarterlyData(quarterlyGraphData)
    };

    fetchData();
  }, [capRD, includeOtherIncome, selectedTax]);

  useEffect(() => {
    const captureCharts = async () => {

      // wait briefly for Recharts to finish rendering
      await new Promise((r) => setTimeout(r, 5500));
      if (graphData.length === 0) return;
      const text = graphData[0].description;
      const pngs = await captureRechartsAsPNGs(chartRefs);
      const tableImage = await captureTableAsPNG(tableRef)

      if (tableImage && pngs[1]) {
        pngs[2] = await combineImagesVertically([
          tableImage,
          pngs[2],
        ]);
      }

      setImages(pngs);
      if (onImagesReady) onImagesReady(pngs);
      if (descriptionData) descriptionData(text);
    };

    captureCharts();
  }, [graphData, scorecard]);

const handleStockDate = (range) => {
  setDateRange(range)
};

useEffect(() => {
  const changeRange = async () => {
    if (!dividendPrice || dividendPrice.length === 0) return;

    let filtered = [...dividendPrice]; // default = ALL

    switch (dateRange) {

      case "5D":
        filtered = twoYearPrice.slice(-5);
        localStorage.setItem('dateRange', '5D')
        break;

      case "1M":
        filtered = twoYearPrice.slice(-23); // ~22 market days
        localStorage.setItem('dateRange', '1M')
        break;

      case "6M":
        filtered = twoYearPrice.slice(-130);
        localStorage.setItem('dateRange', '6M')
        break;

      case "YTD":
        const currentYear = new Date().getFullYear();

        const currentYearData = twoYearPrice.filter(
          p => new Date(p.date).getFullYear() === currentYear
        );

        const lastPrevYear = [...twoYearPrice]
          .filter(p => new Date(p.date).getFullYear() === currentYear - 1)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        filtered = lastPrevYear
          ? [lastPrevYear, ...currentYearData]
          : currentYearData;
        localStorage.setItem('dateRange', 'YTD')
        break;

      case "1Y":
        filtered = twoYearPrice.slice(-259);
        localStorage.setItem('dateRange', '1Y')
        break;

      case "2Y":
        filtered = twoYearPrice;
        localStorage.setItem('dateRange', '2Y')
        break;

      case "5Y":
        filtered = basePrice.slice(-69);
        localStorage.setItem('dateRange', '5Y')
        break;

      case "ALL":
      default:
        filtered = [...basePrice];
        localStorage.setItem('dateRange', 'ALL')
        break;
    }
    setDividendPrice(filtered)
  }


  changeRange()
}, [dateRange])


  // LEFT Y-AXIS (CFIRR) domain
  const leftMin = -10;
  const leftMax = 30;

  // RIGHT Y-AXIS (Stock Price) min/max from data
  const stockValues = stockData.map((d) => d.stockPrice);
  const stockMin = Math.min(...stockValues);
  const stockMax = Math.max(...stockValues);

  // Calculate zero alignment ratio
  const zeroRatio = (0 - leftMin) / (leftMax - leftMin);

  // Adjust right axis so 0 aligns
  const rightMax = stockMax;
  const rightMin = rightMax - (stockMax - stockMin) / (1 - zeroRatio);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const value = payload.value;
    const color = value < 0 ? "red" : "black"; // 👈 red for negatives
    const formattedValue = `${value}%`;


    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fill={color}
        fontSize="10px"
      >
        {formattedValue}
      </text>
    );
  };

  // const lastStockPriceByYear = stockData.reduce((acc, curr) => {
  //   const year = new Date(curr.date).getFullYear();
  //   acc[year] = curr.stockPrice; // overwrite ensures last price of year stays

  //   return acc;
  // }, {});

  // const lastStockPriceByQuarter = stockData.reduce((acc, curr) => {
  //   const date = new Date(curr.date).toLocaleDateString("en-US", {
  //     month: 'numeric',
  //     year: 'numeric'
  //   });

  //   acc[date] = curr.stockPrice; // overwrite ensures last price of year stays
  //   return acc;
  // }, {});

  return (
    <div style={{ width: "100%", height: "100%",  }}>
      {/* Section: CFIRR and Capital Growth */}

      <Typography sx={{ mb: 2, paddingRight: 2 }} fontSize={'small'} gutterBottom>
        {graphData && graphData[0] && graphData[0].description}
      </Typography>

      <Box sx={{ paddingRight: 2, overflowX: 'hidden', overflowY: 'hidden', minHeight: { xs: '3000px', sm: '3000px', md: '2200px', lg: '1800px', xl: '1200px' }}} >
    
      {/* Section: Revenue, Gross Margins & Cash Flow */}
      <Grid 
        container 
        spacing={2} 
      >

      {/* CFIRR Bar Chart */}
      <Grid item xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6} 
          spacing={2}
        >
      <div style={{ width: '100%', height: 430 }} ref={chartRefs[0]}> {/* Flexbox for side-by-side */}
        <Typography
          variant="h6"
          align="left"
          sx={{ fontWeight: 'bold', marginBottom: 1, color: "#4e95d9", }}
        >
          CFIRR Profile
        </Typography>
        <Grid item >
          <Paper sx={{
                height: 350,       
                padding: 1.5,
                borderRadius: 2,
              }}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Please use QuickVALUATION Financials and REITs in Excel for further analysis. 
                You may already have a copy on your desktop or go to the QuickVALUATION menu above and download the latest version.  
              </Typography>
            </Box>
          ) : (
            <>
            <ResponsiveContainer width="100%" height="50%">            
              <ComposedChart data={graphData} margin={{ top: 5, right: 0, bottom: 5, left: 5 }} >
                <XAxis dataKey="date" xAxisId={0} fontSize="10px" hide={true} />
                <XAxis dataKey="date" xAxisId={1} hide={true}  />
                <YAxis
                  orientation="left"
                  yAxisId="left"
                  fontSize="10px"
                  type='number'
                  domain={[-10, 30]}
                  ticks={[-10, -5, 0, 5, 10, 15, 20, 25, 30]}
                  interval={0}
                  allowDataOverflow={true}
                  tick={<CustomYAxisTick />}
                  tickFormatter={(value) => `${value}%`}
                  label={{
                    value: "CFIRR",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000"
                  }}
                />
                <YAxis 
                  padding={{ bottom: 40 }}
                  yAxisId="right" 
                  orientation="right"
                  fontSize="10px"
                  domain={[stockMin, stockMax]}
                  hide={true}
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const date =
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      const cfirrValue = 
                        intangibles === true
                          ? data.cfirrIncl
                          : data.cfirr;

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {date}</div>
                          <div><strong>CFIRR:</strong> {cfirrValue}%</div>
                        </div>
                      );
                    }}
                  />

                <Bar data={(localStorage.getItem('period') === 'Annual' ? graphData : quarterlyData)} dataKey={(getVal('intangibles') ? 'cfirrIncl' : "cfirr")} name="Annual CFIRR" barSize={30} xAxisId={0} yAxisId="left">
                    {(localStorage.getItem('period') === 'Annual' ? graphData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.lastValue ? "#bdd7ee" : "#203864"} // 👈 conditional color
                        stroke="#a7a7a7" 
                        strokeWidth={1}   
                        strokeOpacity={0.8}  
                      />
                    )) : quarterlyData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill="#bdd7ee" // 👈 conditional color
                        stroke="#a7a7a7" 
                        strokeWidth={1}   
                        strokeOpacity={0.8}  
                      />
                    )) )}
                  <LabelList 
                    dataKey={(getVal('intangibles') ? 'cfirrIncl' : "cfirr")} 
                    position="insideTop" 
                    content={(props) => {
                        const { x, y, value, index } = props;

                        if (localStorage.getItem('period') === 'Quarterly') {
                          if (index % 4 !== 0) {
                            return null
                          }
                        }

                        return (
                          <text
                            x={x + 12}
                            y={y}
                            fill={value < 0 ? "red" : "#000000"} // red if negative
                            fontSize={10}
                            fontWeight="bold"
                            textAnchor="middle"
                          >
                            {`${value}%`}
                          </text>
                        );
                      }}
                  />
                </Bar>
                <Area data={stockData} xAxisId={1} yAxisId="right" dataKey="stockPrice" name="Stock Price" stroke="#878786ff" activeDot={false} fillOpacity={2} fill="#ffe19f64" />
              </ComposedChart>
            </ResponsiveContainer>

            <ResponsiveContainer width="100%" height="50%">
              <ComposedChart data={(localStorage.getItem('period') === 'Annual' ? graphData : quarterlyData)} margin={{ top: 5, right: 0, bottom: 5, left: 5 }} >
                <XAxis
                  dataKey="date"
                  hide
                  type="category"
                  allowDuplicatedCategory
                  tick={false}
                  // ticks={
                  //   localStorage.getItem("period") === "Annual"
                  //     ? graphData.map(d => d.date)
                  //     : quarterlyData.map(d => d.date)
                  // }
                />
                <YAxis
                  yAxisId="left"
                  fontSize="10px"
                  domain={[-20, 50]}
                  ticks={[-20, -10, 0, 10, 20, 30, 40, 50]}
                  interval={0}
                  allowDataOverflow={true}
                  tick={<CustomYAxisTick />}
                  tickFormatter={(value) => `${value}%`}
                  label={{
                    value: "Capital Growth",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000",
                  }}
                />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const date = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      const growthValue = 
                        intangibles === true
                          ? data.growthIncl
                          : data.growth;

                      const plowbackValue =
                        intangibles === true
                          ? data.growthTrendIncl
                          : data.growthTrend

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {date}</div>
                          <div><strong>Capital Growth:</strong> {growthValue}%</div>
                          <div><strong>Plowback:</strong> {plowbackValue}%</div>
                        </div>
                      );
                    }}
                  />
                <ReferenceLine 
                  yAxisId="left" 
                  y={0} 
                  stroke="#000" 
                  strokeWidth={1.5}   
                />
                {(localStorage.getItem('period') === 'Annual' ? graphData.map((item, index) => (
                    <ReferenceDot
                      key={index}
                      x={item.date}        // your x-axis value
                      y={0}                // reference line value
                      yAxisId="left"
                      fill="transparent"   // make the dot invisible
                      stroke="transparent" // make the border invisible
                      label={{
                        value: formatDate(item.date),
                        position: 'top',
                        fontSize: 10,
                        fill: '#000000',
                        angle: -90,
                        offset: -30,
                      }}
                    />
                  )) : quarterlyData.map((item, index) => (
                    index % 4 === 0 ?
                    <ReferenceDot
                      key={index}
                      x={item.date}        // your x-axis value
                      y={0}                // reference line value
                      yAxisId="left"
                      fill="transparent"   // make the dot invisible
                      stroke="transparent" // make the border invisible
                      label={{
                        value: formatDate(item.date),
                        position: 'top',
                        fontSize: 10,
                        fill: '#000000',
                        angle: -90,
                        offset: -30,
                      }}
                    /> : null
                  )) )}
                <Bar yAxisId="left" dataKey={(getVal('intangibles') ? 'growthIncl' : "growth")} name="Capital Growth" barSize={30}>
                    {(localStorage.getItem('period') === 'Annual' ? graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#bdd7ee" : "#203864"}
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  )) : quarterlyData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#bdd7ee"
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  )))}
                  <LabelList 
                    dataKey={(getVal('intangibles') ? 'growthIncl' : "growth")} 
                    position="insideTop" 
                    content={(props) => {
                        const { x, y, value, index } = props;

                        if (localStorage.getItem('period') === 'Quarterly') {
                          if (index % 4 !== 0 ) {
                            return null
                          }
                        }

                        return (
                          <text
                            x={x + 12}
                            y={y}
                            fill={value < 0 ? "red" : "#000000"} // red if negative
                            fontSize={10}
                            fontWeight="bold"
                            textAnchor="middle"
                          >
                            {`${value}%`}
                          </text>
                        );
                      }} 
                    />
                </Bar>
                <Line yAxisId="left" dataKey={(getVal('intangibles') ? 'growthTrendIncl' : "growthTrend")} name="Plowback" stroke="#ff7300" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            </>
            )}
          </Paper>
        </Grid>
      </div>
      </Grid>

      {/* Scorecard */}
      <Grid item xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6} 
          spacing={2}
        >
          <Typography
              variant="h6"
              gutterBottom
              sx={{
                  fontWeight: "bold",
                  color: "#4e95d9",
                  position: "sticky",
                  left: 0,
              }}
          >
            Scorecard 
          </Typography>  
        <div ref={chartRefs[1]}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <Scorecard scorecardLoaded={setScorecard} />
          )}
        </div>
      </Grid>

       {/* Stock Price Area Chart */}
        <Grid item xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6} 
          spacing={2}
        >
        <div style={{ width: '100%', height: "100%", marginBottom: 12 }} ref={chartRefs[2]} > {/* Flexbox for side-by-side */}
        <Grid item spacing={2}>
            <Paper sx={{ 
              height: 300,
              px: 1
              }}>
              {twoYearPrice && basePrice && (
                <ReturnsTable
                  twoYearData={twoYearPrice}
                  data={basePrice}
                  ref={tableRef}
                />
              )}
              {buttonValues.map((value) => (
                <Button varient='contained'   
                  sx={{
                      minWidth: "38px",        
                      width: "38px",          
                      height: "28px",         
                      p: "0px",              
                      fontSize: "12px",      
                      lineHeight: 1,          
                      fontWeight: "bold",
                      backgroundColor: value === dateRange ? "#f0f9ff" : "#fff",
                      color: value === dateRange ? "#1966d2" : "black",
                    }}
                 key={value} onClick={() => handleStockDate(value)}>{value}</Button>
              ))}
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dividendPrice} syncId="stockSync" margin={{ top: 10, right: 0, bottom: 100, left: 0 }}>
                  <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                  <YAxis  
                    yAxisId="left" 
                    orientation="left" 
                    fontSize="10px"
                    tickFormatter={(value) => `$${formatCurrency(value)}`}
                    label={{ 
                      value: "Stock Price", 
                      angle: -90, 
                      dx: -20,
                      position: "outsideLeft", 
                      style: { 
                        textAnchor: "middle", 
                        fontSize: "12px", 
                        fontWeight: "bold", 
                        fill: "#000000" 
                      } 
                    }} 
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    hide={true}
                    domain={([dataMin, dataMax]) => {
                      return [(dataMin), (dataMax * 2)];
                    }}
                  />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const date = new Date(data.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>Date: </strong>{date}</div>
                          <div><strong>Reinvested Dividends: </strong>${formatCurrency(data.ReinvestedDividends)}</div>
                          <div><strong>Excluding Dividends: </strong>${formatCurrency(data.ExcludingDividends)}</div>
                          <div><strong>Volume: </strong>{formatCurrency(data.volume)} M</div>
                        </div>
                      );
                    }}
                  />
                  <Legend 
                  wrapperStyle={{
                    position: 'relative',
                    bottom: 120
                  }}
                    formatter={(value, entry) => (
                      <span
                        style={{
                          color: "#000",
                          fontSize: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                          borderRadius: "4px",
                          padding: "2px 6px",
                        }}
                      >
                        {value}
                      </span>
                    )} 
                  />
                  <Area type="monotone" yAxisId="left" stackId='b' dataKey="ReinvestedDividends" name="Reinvested Dividends" stroke="#5b9bd5" fillOpacity={1} fill="#5b9bd5" />
                  <Area type="monotone" yAxisId="left" stackId='a' dataKey="ExcludingDividends" name="Excluding Dividends" stroke="#bdd7ee" fillOpacity={1} fill="#bdd7ee" />
                  <Bar yAxisId="right" dataKey="volume" name="Volume" fill="#b0b0b0ff" />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </div>
        </Grid>

        <Grid item  
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6}  
        >
        <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[3]} > {/* Flexbox for side-by-side */}
          <Paper sx={{
              height: 300,
              px: 1}}
            >
            {financialIndustries.includes(industryNumber) ? (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  textAlign: 'center',
                  padding: 2
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Not Applicable  
                </Typography>
              </Box>
            ) : (
            <>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={graphData} margin={{ top: 20, right: 0, bottom: 20, left: 5 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis
                  fontSize="10px"
                  tickFormatter={(value) => `$${formatCurrency(value)}`}
                  label={{
                    value: "($) Million",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000",
                  }}
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>Revenue: </strong>${formatCurrency(data.totalRevenue)}</div>
                          <div><strong>Gross Margin: </strong>${formatCurrency(data.grossMargin)}</div>
                          <div><strong>Cash Flow from Ops (CFOPS): </strong>${formatCurrency(data.cashFlow)}</div>
                        </div>
                      );
                    }}
                  />
                <Legend 
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                
                <Bar dataKey="cashFlow" name="Cash Flow From Ops (CFOPS)" stackId="a" fill="#548235" >
                    {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#2f5597" : "#548235"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                </Bar> 
                <Bar dataKey="grossMargin" name="Gross Margin" stackId="a" fill="#c5e0b4" >
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#b4c7e7" : "#c5e0b4"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                </Bar>
                <Bar dataKey="totalRevenue" name="Revenue" stackId="a" fill="#e2f0d9" >
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#dae3f3" : "#e2f0d9"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </>
            )}
          </Paper>
        </div>
        </Grid>

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6}  
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[4]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={graphData} padding={{ top: 40, right: 20, bottom: 40, left: 20 }} margin={{ top: 20, right: 0, bottom: 20, left: 5 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis
                  yAxisId="left"
                  fontSize="10px"
                  tickFormatter={(value) => `$${value}`}
                  label={{
                    value: "CFOPS / Shr",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000",
                  }}
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>Cash Flow Per Share: </strong>${data.cashFlowShare}</div>
                        </div>
                      );
                    }}
                  />
                <Bar yAxisId="left" dataKey="cashFlowShare" stroke="#333">
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#2f5597" : "#045513ff"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                  <LabelList dataKey="cashFlowShare" position="top" angle={-90} offset={15} dx={5} fontSize="10px" fontWeight="bold" fill='#000000' formatter={(v) => `$${v}`} />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
            </>
          )}
          </Paper>
          </div>
        </Grid>

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6} 
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[5]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graphData} padding={{ top: 40, right: 20, bottom: 40, left: 20 }} margin={{ top: 40, right: 15, bottom: 20, left: 5 }}>
                <YAxis
                  yAxisId="left"
                  fontSize="10px"
                  tickFormatter={(value) => `${value}%`}
                  label={{
                    value: "Margins (%)",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000",
                  }}
                />
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>Gross Margin: </strong>{data.grossMarginPercent}%</div>
                          <div><strong>Cash Flow Margin: </strong>{data.cashFlowPercent}%</div>
                        </div>
                      );
                    }}
                  />
                <Legend 
                  wrapperStyle={{
                      position: 'relative',
                      bottom: 35
                    }}
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                <Line
                  yAxisId="left"
                  dataKey="grossMarginPercent"
                  name="Gross Margin %"
                  stroke="#e0eed6"
                  strokeWidth={3}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    const fillColor = payload.lastValue ? "#b4c7e7" : "#e0eed6";
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={2}
                        stroke="#a7a7a7"
                        strokeWidth={1}
                        strokeOpacity={0.8}
                        fill={fillColor}
                      />
                    );
                  }}
                >
                    <LabelList dataKey="grossMarginPercent" position="insideTop" angle={-90} offset={-25} dx={5} fontSize="10px" fontWeight="bold" fill='#000000' formatter={(v) => `${v}%`} />
                </Line>
                <Line 
                  yAxisId="left" 
                  dataKey="cashFlowPercent" 
                  name="Cash Flow Margin %" 
                  stroke="#a2bb92" 
                  strokeWidth={3} 
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    const fillColor = payload.lastValue ? "#2f5597" : "#e0eed6";
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={2}
                        stroke="#a7a7a7"
                        strokeWidth={1}
                        strokeOpacity={0.8}
                        fill={fillColor}
                      />
                    );
                  }}
                  >
                  <LabelList dataKey="cashFlowPercent" position="insideTop" angle={-90} offset={5} dx={5} fontSize="10px" fontWeight="bold" fill='#000000' formatter={(v) => `${v}%`} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
            </>
          )}
          </Paper>
        </div>
        </Grid>

      {/* Section: Multiples */}

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6}  
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[6]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={graphData} margin={{ top: 20, right: 0, bottom: 20, left: 5 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis
                  yAxisId="left"
                  fontSize="10px"
                  tickFormatter={(value) => `${value}.0x`}
                  label={{
                    value: "Multiple",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                    fontSize: "12px",
                    fontWeight: "bold",
                    fill: "#000000",
                  }}
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>EV / EBITDA: </strong>{data.evOverEbitda}x</div>
                          <div><strong>EV / CF: </strong>{data.evOverCashFlow}x</div>
                        </div>
                      );
                    }}
                  />
                <Legend
                  wrapperStyle={{
                        position: 'relative',
                        bottom: 35
                      }}
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                <Bar yAxisId="left" dataKey="evOverEbitda" name="EV / EBITDA" fill="#045513ff" stroke="#333">
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#2f5597" : "#045513ff"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                  <LabelList dataKey="evOverEbitda" position="top" angle={-90} offset={20} dx={5} fontSize="10px" fontWeight="bold" fill='#000000' formatter={(v) => `$${v}x`} />
                </Bar>
                <Bar yAxisId="left" dataKey="evOverCashFlow" name="EV / CF" stroke="#333" fill="#a9d18e" >
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#8faadc" : "#a9d18e"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                  {/* <LabelList dataKey="evOverCashFlow" position="top" angle={-90} offset={20} dx={5} fontSize="9px" fontWeight="bold" fill='#000000' formatter={(v) => `$${v}x`} /> */}
                </Bar>
                <ReferenceLine yAxisId="left" y={0} stroke="#000" strokeWidth={1} />
              </ComposedChart>
            </ResponsiveContainer>
            </>
          )}
          </Paper>
          </div>
        </Grid>

      {/* Section: Dividends and Capital Structure */}

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6}  
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[7]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dividendData} margin={{ top: 20, right: 0, bottom: 20, left: 5 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  fontSize="10px"
                  domain={[0, 1]}
                  tickFormatter={(value) => `$${value}`}
                  label={{ 
                    value: "Qtrly Dividends / Shr", 
                    dy: 20,
                    angle: -90, 
                    position: "insideLeft", 
                    style: { textAnchor: "middle", fontSize: "12px", fontWeight: "bold", fill: "#000000" } 
                  }} 
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  fontSize="10px" 
                  domain={[0, 10]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Legend 
                  wrapperStyle={{
                        position: 'relative',
                        bottom: 35
                      }}
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>Quarterly Dividends: </strong>${data.dividendPerShare}</div>
                          <div><strong>Dividend Yield: </strong>{data.dividendYield}%</div>
                        </div>
                      );
                    }}
                  />
                <Bar yAxisId="left" dataKey="dividendPerShare" name="Quarterly Dividends" barSize={30} fill="#0070c0" />
                <Line yAxisId="right" dataKey="dividendYield" name="Dividend Yield" stroke="#ff6600" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
          </div>
        </Grid>

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6} 
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[8]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={graphData} margin={{ top: 20, right: 0, bottom: 10, left: 5 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left"
                  fontSize="10px" 
                  tickFormatter={(value) => `$${formatCurrency(value)}`}
                  offset={-10}
                  label={{ 
                    value: "Debt & Market Cap", 
                    angle: -90, 
                    position: "insideLeft",
                    style: { textAnchor: "middle", fontSize: "12px", fontWeight: "bold", fill: "#000000" } 
                  }} 
                />
                <YAxis yAxisId="right" orientation="right" fontSize="10px" domain={[0, 0.4]} tickFormatter={(value) => `${value}x`}/>
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>{data.lastValue ? "TFQ:" : "Year:"}</strong> {year}</div>
                          <div><strong>Debt & Equiv: </strong>${formatCurrency(data.debtAndEquivalents)}</div>
                          <div><strong>Market Equity: </strong>${formatCurrency(data.marketCap)}</div>
                          <div><strong>Debt / Equity: </strong>{data.debtOverMarketCap}x</div>
                        </div>
                      );
                    }}
                  />
                <Legend 
                  wrapperStyle={{
                      position: 'relative',
                      bottom: 35
                    }}
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                <Bar yAxisId="left" dataKey="debtAndEquivalents" name="Debt & Equiv" stackId="a" barSize={30} fill="rgba(255,0,0,1)">
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#2f5597" : "rgba(255,0,0,1)"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                </Bar>
                <Bar yAxisId="left" dataKey="marketCap" name="Market Equity" stackId="a" barSize={30} fill="#045513ff" >
                  {graphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.lastValue ? "#8faadc" : "#045513ff"} // 👈 conditional color
                      stroke="#a7a7a7"      
                      strokeWidth={1}    
                      strokeOpacity={0.8}   
                    />
                  ))}
                </Bar>
                <Line yAxisId="right" dataKey="debtOverMarketCap" name="Debt / Equity" stroke="#a5a5a5" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            </>
          )}
          </Paper>
          </div>
        </Grid>

        <Grid item 
          xs={12} 
          sm={12} 
          md={10} 
          lg={6} 
          xl={6}  
        >
          <div style={{ width: '100%', height: 250, marginBottom: 12 }} ref={chartRefs[9]} > {/* Flexbox for side-by-side */}
          <Paper sx={chartPaperStyle}>
          {financialIndustries.includes(industryNumber) ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                padding: 2
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Not Applicable  
              </Typography>
            </Box>
          ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dividendData} margin={{ top: 40, right: 0, bottom: 10, left: 20 }}>
                <XAxis dataKey="date"
                  tickFormatter={(value) => `${formatDate(value)}`}
                  position= 'bottom' 
                  fontSize = '10' 
                  fill ='#000000' 
                  angle = '-90' 
                  dy={10}   
                  tickLine={false}               
                />
                <YAxis yAxisId="left" orientation="left" fontSize="10px" tickFormatter={(value) => `$${value}`} >
                  <Label angle={-90} position="insideLeft" content={({ viewBox }) => {
                    const { x, y, height } = viewBox;
                    return (
                      <g transform={`translate(${x - 25}, ${y + height / 2}) rotate(-90)`}>
                        <text style={{ fontSize: "12px", fontWeight: "bold", textAnchor: "middle" }}>
                          <tspan x="0" dy="1.2em">Rolling TFQ Cash</tspan>
                          <tspan x="0" dy="1.2em">Flow and Dividends</tspan>
                        </text>
                      </g>
                    );
                  }} />
                </YAxis>
                <YAxis yAxisId="right" orientation="right" fontSize="10px" domain={[0, 100]} tickFormatter={(value) => `${value}%`}/>
                <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const data = payload[0].payload; // graphData item
                      const year = 
                        data.lastValue
                          ? new Date(data.date).getFullYear()
                          : new Date(data.date).toLocaleDateString('en-US', {
                            month: "short",
                            year: "numeric"
                          })

                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(50, 50, 50, 0.6)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            borderRadius: "6px",
                            padding: "6px",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <div><strong>Year: </strong>{year}</div>
                          <div><strong>TFQ Dividends PS: </strong>${data.tfqDividends}</div>
                          <div><strong>TFQ Cash Available PS: </strong>${data.cashAvailable}</div>
                          <div><strong>Payout Ratio: </strong>{data.payoutRatio}%</div>
                        </div>
                      );
                    }}
                  />
                <Legend
                  formatter={(value, entry) => (
                    <span
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      {value}
                    </span>
                  )} 
                />
                <Bar yAxisId="left" dataKey="tfqDividends" name="TFQ Dividends PS" barSize={30} stackId="a" fill="#0c62d3ff" />
                <Bar yAxisId="left" dataKey="cashAvailable" name="TFQ Cash Available PS" barSize={30} stackId="a" fill="#00b050" />
                <Line yAxisId="right" dataKey="payoutRatio" name="Payout Ratio" stroke="#ffc000" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            </>
          )}
          </Paper>
          </div>
        </Grid>
      </Grid>
    </Box>
  
    </div>
  );
};

export default Graphs;